'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { type FocusEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { ThumbsIcon } from '@/components/icons'
import { SubmitButton } from '@/components/submit-button'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GoogleLoginButton } from '@/features/auth/components/auth/google-login-button'
import { useAuth, useAuthDialogControl, usePasskey } from '@/features/auth/hooks'
import { useResetClientStorage } from '@/features/auth/hooks/use-reset-client-storage'
import { navigateAfterAuth } from '@/features/auth/lib/post-auth-navigation'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { fetchUserAccounts } from '@/service/account.api'
import { useResendVerificationEmail, useSignupWithEmail } from '@/service/auth.hook'
import { userProfileKeys } from '@/service/user-profile.hook'
import { emailResendAtom } from '@/state/auth'
import { emitAuthAnalytics } from '@/state/auth-analytics'
import { AccountStatus } from '@/types/generated/common/account'

interface Props {
  className?: string
  mode: 'login' | 'signup'
  onSwitchMode?: () => void
  onEmailVerifyNeeded?: () => void
}

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
})

const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
})

type LoginFormData = z.infer<typeof loginSchema>
type SignupFormData = z.infer<typeof signupSchema>
type FormData = LoginFormData | SignupFormData

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function AuthForm({ className, mode, onSwitchMode, onEmailVerifyNeeded }: Props) {
  // Local variables
  const authT = useTranslations('auth')
  const commonT = useTranslations('common')
  const queryClient = useQueryClient()
  const setEmailResend = useSetAtom(emailResendAtom)
  const router = useRouter()
  const searchParams = useSearchParams()

  const { loginWithEmail, isLoading, handlePasskeyResponse } = useAuth()
  const resetClientStorage = useResetClientStorage()
  const { loginWithPasskey } = usePasskey()
  const { isOpen, handleSuccess } = useAuthDialogControl()

  const [isPasskeyWorking, setIsPasskeyWorking] = useState(false)
  const [isForcedTimeout, setIsForcedTimeout] = useState(false)
  const [didSubmit, setDidSubmit] = useState(false)
  const loginWithPasskeyRef = useRef(loginWithPasskey)
  const completePasskeyLoginRef = useRef<
    | null
    | ((response: Awaited<ReturnType<typeof loginWithPasskey>>, silent?: boolean) => Promise<void>)
  >(null)
  const hasArmedConditionalPasskeyRef = useRef(false)
  const conditionalPasskeyAbortControllerRef = useRef<AbortController | null>(null)

  const form = useForm<FormData>({
    resolver: zodResolver(mode === 'login' ? loginSchema : signupSchema),
    mode: 'onChange',
    defaultValues: { email: searchParams.get('email') || '' },
  })

  // Derived variables
  const callbackUrl = searchParams.get('callbackUrl') || '/'
  const isActuallySubmitting = isLoading || form.formState.isSubmitting
  const emailField = form.register('email')

  // Callbacks and methods
  const handleAuthComplete = useCallback(
    async (isNewUser = false) => {
      // Identity + Log in / Sign up analytics live in AnalyticsSync, fed by the
      // auth hooks' success paths — nothing to do for analytics here.
      await handleSuccess(isNewUser)

      if (!isOpen) {
        try {
          const accountsData = await fetchUserAccounts()
          const hasActiveAccount = accountsData.accounts.some(
            (acc) => acc.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
          )

          if (isNewUser || !hasActiveAccount) {
            navigateAfterAuth('/onboarding')
          } else {
            navigateAfterAuth(callbackUrl)
          }
        } catch (error) {
          console.error('Failed to fetch accounts during auth complete:', error)
          navigateAfterAuth('/onboarding')
        }
      }
    },
    [callbackUrl, handleSuccess, isOpen]
  )

  // `silent` covers the conditional-mediation autofill path (armed
  // automatically on mount, below) — a failure there (e.g. the backend
  // reporting the session is already authenticated) is not something the
  // user asked for or needs to see, matching the outer WebAuthn-level catch's
  // own "unprompted background attempt" comment. The explicit button click
  // (handlePasskeyLogin) keeps the toast — that one really was user-initiated.
  const completePasskeyLogin = useCallback(
    async (response: Awaited<ReturnType<typeof loginWithPasskey>>, silent = false) => {
      await handlePasskeyResponse(response)
      if (response.success) {
        resetClientStorage()
        await queryClient.invalidateQueries({ queryKey: userProfileKeys.passkeys() })
        await handleAuthComplete()
      } else if (!silent) {
        toast.error(response.errorMessage || authT('passkeyLoginFailed'))
      }
    },
    [authT, handleAuthComplete, handlePasskeyResponse, queryClient, resetClientStorage]
  )

  const abortConditionalPasskeyLogin = useCallback(() => {
    conditionalPasskeyAbortControllerRef.current?.abort()
    conditionalPasskeyAbortControllerRef.current = null
  }, [])

  const { mutateAsync: signupWithEmail } = useSignupWithEmail({
    onSuccess: async (data) => {
      const email = data.user?.email || form.watch('email')
      // The single Sign up path: emitted from the response's userId so it also
      // covers the unverified-email branch (no auth state yet), and consumed once
      // by AnalyticsSync (identify → track).
      if (data.user?.userId) {
        emitAuthAnalytics({ method: 'email', userId: data.user.userId, kind: 'signup' })
      }
      if (data.user?.emailVerified) {
        await loginWithEmail({
          email,
          password: form.watch('password'),
          skipAnalytics: true,
        })
        // Wait briefly for cookie to be set before navigation (SSR race condition fix)
        await new Promise((resolve) => setTimeout(resolve, 100))
        await handleAuthComplete(true) // Signup always means new user
      } else {
        setEmailResend({
          email: form.watch('email'),
          password: form.watch('password'),
          cooldown: Date.now() + 60 * 1000,
        })
        if (onEmailVerifyNeeded) {
          onEmailVerifyNeeded()
        } else {
          router.push('/email-verify?callbackUrl=' + encodeURIComponent('/onboarding'))
        }
        form.reset()
      }
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        toast.error(error.response?.data.message)
      } else {
        toast.error(error.message)
      }
    },
  })

  const { mutateAsync: resendVerificationEmail } = useResendVerificationEmail({
    onSuccess: (data) => {
      if (data.success) {
        setEmailResend({
          email: form.watch('email'),
          cooldown: Date.now() + 60 * 1000,
          password: form.watch('password'),
        })
        toast.success(authT('verificationEmailSent'))
        if (onEmailVerifyNeeded) {
          onEmailVerifyNeeded()
        } else {
          router.push(`/email-verify?callbackUrl=${encodeURIComponent(callbackUrl)}`)
        }
      }
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        toast.error(error.response?.data.message)
      } else {
        toast.error(error.message)
      }
    },
  })

  const handlePasskeyLogin = async () => {
    setIsPasskeyWorking(true)
    setDidSubmit(true)
    try {
      const response = await loginWithPasskey({})
      await completePasskeyLogin(response)
    } catch (e) {
      if (!isAbortError(e)) {
        toast.error(authT('passkeyVerificationFailed'))
      }
    } finally {
      setIsPasskeyWorking(false)
    }
  }

  const handleEmailBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      abortConditionalPasskeyLogin()
      emailField.onBlur(event)
    },
    [abortConditionalPasskeyLogin, emailField]
  )

  const onSubmit = async (data: FormData) => {
    setDidSubmit(true)
    if (mode === 'signup') {
      await signupWithEmail(data)
    } else {
      try {
        const { requires2fa, requiresEmailVerification } = await loginWithEmail({
          email: data.email,
          password: data.password,
        })
        resetClientStorage()
        // Wait briefly for cookie to be set before navigation (SSR race condition fix)
        await new Promise((resolve) => setTimeout(resolve, 100))
        if (requires2fa) {
          router.push(`/mfa?callbackUrl=${encodeURIComponent(callbackUrl)}`)
        } else if (requiresEmailVerification) {
          await resendVerificationEmail({ email: data.email })
        } else {
          await handleAuthComplete()
        }
      } catch (error) {
        if (isAxiosError(error)) {
          toast.error(error.response?.data.message)
        } else {
          toast.error((error as Error).message)
        }
      }
    }
  }

  // Effects
  useEffect(() => {
    loginWithPasskeyRef.current = loginWithPasskey
    completePasskeyLoginRef.current = completePasskeyLogin
  }, [completePasskeyLogin, loginWithPasskey])

  // Conditional UI: silently arm the passkey autofill so the browser shows a
  // suggestion in the email field only if a matching passkey actually exists
  // (no explicit detection call needed — the browser does that for us).
  useEffect(() => {
    if (mode !== 'login') {
      abortConditionalPasskeyLogin()
      hasArmedConditionalPasskeyRef.current = false
      return
    }
    if (hasArmedConditionalPasskeyRef.current) return
    if (
      typeof window === 'undefined' ||
      !window.PublicKeyCredential?.isConditionalMediationAvailable
    ) {
      return
    }

    const controller = new AbortController()
    conditionalPasskeyAbortControllerRef.current = controller
    hasArmedConditionalPasskeyRef.current = true

    PublicKeyCredential.isConditionalMediationAvailable()
      .then((isAvailable) => {
        if (!isAvailable || controller.signal.aborted) return
        return loginWithPasskeyRef
          .current({
            mediation: 'conditional',
            abortSignal: controller.signal,
          })
          .then((response) => completePasskeyLoginRef.current?.(response, true))
      })
      .catch((e) => {
        if (!isAbortError(e)) {
          // Silent: this is an unprompted background attempt, not a user-initiated one.
        }
      })
      .finally(() => {
        if (conditionalPasskeyAbortControllerRef.current === controller) {
          conditionalPasskeyAbortControllerRef.current = null
        }
      })

    return () => {
      controller.abort()
      if (conditionalPasskeyAbortControllerRef.current === controller) {
        conditionalPasskeyAbortControllerRef.current = null
      }
    }
  }, [abortConditionalPasskeyLogin, mode])

  useEffect(() => {
    if (!isActuallySubmitting) {
      setIsForcedTimeout(false)
      setDidSubmit(false)
      return
    }

    // Only start the 5s timer if the submission was user-initiated
    if (!didSubmit) return

    const timer = setTimeout(() => {
      if (isActuallySubmitting) {
        setIsForcedTimeout(true)
        toast.error(authT('requestTakingLonger'))
      }
    }, 5000)

    return () => clearTimeout(timer)
  }, [authT, isActuallySubmitting, didSubmit])

  return (
    <form
      className={cn(['flex flex-col gap-4 px-6', className])}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      {/* Social / Passkey buttons — grid on login, single on signup */}
      {mode === 'login' ? (
        <div className="grid grid-cols-2 gap-2">
          <GoogleLoginButton
            className="border-foreground/15 hover:bg-foreground/5"
            mode={mode}
            callbackUrl={callbackUrl}
          />
          <Button
            type="button"
            variant={'outline'}
            disabled={isPasskeyWorking}
            className="border-foreground/15 hover:bg-foreground/5 disabled:bg-foreground/5 bg-transparent disabled:opacity-80"
            onClick={async (e) => {
              e.preventDefault()
              await handlePasskeyLogin()
            }}
          >
            <div className="flex items-center gap-2 font-sans text-sm font-medium tracking-tight">
              <ThumbsIcon className="size-4" />
              {isPasskeyWorking ? authT('checking') : authT('passkey')}
            </div>
          </Button>
        </div>
      ) : (
        <GoogleLoginButton
          className="border-foreground/15 hover:bg-foreground/5"
          mode={mode}
          callbackUrl={callbackUrl}
        />
      )}

      {/* OR divider */}
      <div className="relative flex w-full items-center gap-4">
        <span className="bg-foreground/10 h-px w-full flex-1" />
        <p className="text-foreground/50 text-xs font-medium tracking-tight uppercase">
          {authT('or')}
        </p>
        <span className="bg-foreground/10 h-px w-full flex-1" />
      </div>

      {/* Email / Password inputs */}
      <div className="flex flex-col gap-2">
        <Input
          autoComplete={mode === 'login' ? 'username webauthn' : 'email'}
          autoFocus
          id="email"
          placeholder={authT('email')}
          required
          type="email"
          {...emailField}
          onBlur={handleEmailBlur}
        />
        <Input
          id="password"
          required
          type="password"
          placeholder={authT('password')}
          {...form.register('password')}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        />
      </div>

      <SubmitButton isSuccessful={didSubmit && isActuallySubmitting && !isForcedTimeout}>
        {mode === 'login' ? authT('signIn') : authT('signUpAction')}
      </SubmitButton>

      <div
        className={cn([
          'my-5 flex items-center justify-center gap-4 font-medium tracking-tight',
          mode === 'signup' && 'hidden',
        ])}
      >
        {onSwitchMode ? (
          <button
            type="button"
            onClick={onSwitchMode}
            className="text-foreground/70 hover:text-foreground relative text-sm underline-offset-2 before:absolute before:-inset-x-2 before:-inset-y-3 before:content-[''] hover:underline"
          >
            {authT('signUp')}
          </button>
        ) : (
          <Link
            href="/register"
            className="text-foreground/70 hover:text-foreground relative text-sm underline-offset-2 before:absolute before:-inset-x-2 before:-inset-y-3 before:content-[''] hover:underline"
          >
            {authT('signUp')}
          </Link>
        )}
        <Link
          href="/password"
          className="text-foreground/70 hover:text-foreground relative text-sm underline-offset-2 before:absolute before:-inset-x-2 before:-inset-y-3 before:content-[''] hover:underline"
        >
          {authT('forgotPassword')}
        </Link>
      </div>

      <div className={cn(['text-foreground/40 mx-auto mt-7 text-center text-sm tracking-tight'])}>
        {authT('signupAgreementPrefix')}{' '}
        <Link
          href="https://orderx.com/general-t-c"
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-3"
        >
          {commonT('termsOfUse')}
        </Link>{' '}
        {authT('signupAgreementAnd')}{' '}
        <Link
          href="https://orderx.com/privacy-policy"
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-3"
        >
          {commonT('privacyPolicy')}
        </Link>
        .
      </div>
      {mode === 'signup' && (
        <div className="flex items-center justify-center gap-4 text-sm font-medium tracking-tight">
          <p>{authT('alreadyHaveAccount')}</p>
          {onSwitchMode ? (
            <button type="button" onClick={onSwitchMode} className="text-foreground text-sm">
              {authT('loginTitle')}
            </button>
          ) : (
            <Link href="/login" className="text-foreground text-sm">
              {authT('loginTitle')}
            </Link>
          )}
        </div>
      )}
    </form>
  )
}
